import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Award, Plus, Search, Trash2, TriangleAlert } from 'lucide-react';
import type {} from '@malgoos/shared';
import { Abas, PainelAba } from '../../componentes/abas';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Erro, Estado, Vazio } from '../../componentes/estados';
import { Entrada, Selecao } from '../../componentes/formulario';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { PARAMETRO_TELA_DA_VISAO } from '../../componentes/compartilhar-visao';
import { useToast } from '../../componentes/toast';
import { del, get, mensagemDeErro, post, getTodos } from '../../nucleo/api';
import { data as fmtData, nomePessoa } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { BASE, BarraProgresso, BotaoIcone } from './comum';
import type { Curso, Eu, Matricula, ProgressoPessoa, ProgressoPorCurso } from './tipos';
import { TextoCortado } from '../../componentes/texto-cortado';

/** M11.4 e M11.7: progresso do usuário logado e, para quem gerencia, por pessoa e por curso. */
export function Progresso() {
  const { t } = useTranslation('treinamento');
  const s = useSessao();
  // Review r38 spec (B3): o cartão de Relatórios de uma visão do "Por cursos" leva a tela no link; abre nessa sub-aba.
  const [url] = useSearchParams();
  const [visao, setVisao] = useState<'pessoas' | 'cursos'>(() => (url.get(PARAMETRO_TELA_DA_VISAO) === 'treinamento.progresso.cursos' ? 'cursos' : 'pessoas'));
  const [pessoa, setPessoa] = useState<ProgressoPessoa | null>(null);
  const [curso, setCurso] = useState<ProgressoPorCurso | null>(null);
  // Val5 (29/10): o progresso da equipe tem ação própria (`treinamento.acompanhar`).
  const podeEditar = s.pode('treinamento.acompanhar');
  const eu = useQuery({ queryKey: ['treinamento', 'eu'], queryFn: () => get<Eu>('/treinamento/eu') });

  const colunasPessoa: Coluna<ProgressoPessoa>[] = [
    { chave: 'nome', rotulo: t('campos.funcionario'), principal: true, render: (x) => <span className="font-medium">{nomePessoa(x.nome)}</span> },
    { chave: 'funcao_nome', rotulo: t('campos.funcao'), render: (x) => x.funcao_nome ?? '-' },
    { chave: 'cursos_concluidos', rotulo: t('progresso.concluidos'), alinhar: 'direita', mono: true },
    { chave: 'em_andamento', rotulo: t('progresso.emAndamento'), alinhar: 'direita', mono: true },
    { chave: 'percentual', rotulo: t('progresso.percentual'), render: (x) => <div className="w-36"><BarraProgresso valor={x.percentual} /></div> },
  ];
  const colunasCurso: Coluna<ProgressoPorCurso>[] = [
    { chave: 'titulo', rotulo: t('campos.curso'), principal: true, render: (x) => <span className="font-medium">{x.titulo}</span> },
    { chave: 'aulas', rotulo: t('progresso.aulas'), alinhar: 'direita', mono: true },
    { chave: 'matriculados', rotulo: t('progresso.matriculados'), alinhar: 'direita', mono: true },
    { chave: 'concluidos', rotulo: t('progresso.concluidos'), alinhar: 'direita', mono: true },
    { chave: 'em_andamento', rotulo: t('progresso.emAndamento'), alinhar: 'direita', mono: true },
    { chave: 'percentual_medio', rotulo: t('progresso.percentualMedio'), render: (x) => <div className="w-36"><BarraProgresso valor={x.percentual_medio} /></div> },
  ];

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <MeuProgresso eu={eu.data ?? null} carregando={eu.isPending} erro={eu.isError ? eu.error : undefined} aoTentar={() => void eu.refetch()} />

      {podeEditar && (
        <section aria-label={t('progresso.equipe')} className="flex min-w-0 flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-sm font-semibold">{t('progresso.equipe')}</h2>
            <Abas idBase="progresso-visao" rotulo={t('progresso.equipe')} ativa={visao} aoMudar={setVisao} itens={(['pessoas', 'cursos'] as const).map((v) => ({ chave: v, rotulo: t(`progresso.por_${v}`) }))} />
          </div>
          <PainelAba idBase="progresso-visao" chave={visao} className="min-w-0">
            {visao === 'pessoas' ? (
              <Tabela<ProgressoPessoa> key="pessoas" tela="treinamento.progresso.pessoas" caminho="/treinamento/progresso/pessoas" chaveConsulta={['treinamento', 'progresso', 'pessoas']} colunas={colunasPessoa} aoClicar={setPessoa} vazio={<Vazio texto={t('vazio.progressoPessoas')} />} />
            ) : (
              <Tabela<ProgressoPorCurso> key="cursos" tela="treinamento.progresso.cursos" caminho="/treinamento/progresso/cursos" chaveConsulta={['treinamento', 'progresso', 'cursos']} colunas={colunasCurso} aoClicar={setCurso} vazio={<Vazio texto={t('vazio.progressoCursos')} />} />
            )}
          </PainelAba>
        </section>
      )}

      <VerificarCodigo />
      {pessoa && <PainelPessoa pessoa={pessoa} aoFechar={() => setPessoa(null)} />}
      {curso && <PainelCurso curso={curso} aoFechar={() => setCurso(null)} />}
    </div>
  );
}

function SeloOrigem({ m }: { m: Matricula }) {
  const { t } = useTranslation('treinamento');
  return m.origem === 'trilha' ? <Selo>{m.trilha_nome ? t('selos.trilhaNome', { nome: m.trilha_nome }) : t('selos.trilha')}</Selo> : <Selo>{t('selos.manual')}</Selo>;
}

function MeuProgresso({ eu, carregando, erro, aoTentar }: { eu: Eu | null; carregando: boolean; erro?: unknown; aoTentar?: () => void }) {
  const { t } = useTranslation('treinamento');
  const navegar = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['treinamento', 'matriculas', 'minhas', eu?.funcionario?.id ?? null],
    queryFn: () => getTodos<Matricula>('/treinamento/matriculas', { funcionario_id: eu?.funcionario?.id }),
    enabled: !!eu?.funcionario,
  });
  const emitir = useMutation({
    mutationFn: (cursoId: string) => post<{ codigo: string }>('/treinamento/certificados', { curso_id: cursoId }),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ['treinamento'] });
      toast.sucesso(t('mensagens.certificadoEmitido'));
      navegar(`${BASE}/certificado/${r.codigo}`);
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  return (
    <Cartao titulo={t('progresso.meu')} semPadding>
      {carregando ? (
        <p className="p-4 text-sm text-tinta-3">…</p>
      ) : erro ? (
        // Falha da API (500, 503, rede) não é "usuário sem vínculo": mostra o erro e deixa tentar de novo.
        <Erro erro={erro} aoTentar={aoTentar} />
      ) : !eu?.funcionario ? (
        <p role="status" className="flex items-start gap-2 p-4 text-sm text-tinta-2">
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-laranja" aria-hidden />
          <span>{t('area.semFuncionario')}</span>
        </p>
      ) : (
        <Estado consulta={q}>
          {(d) =>
            d.itens.length === 0 ? (
              <Vazio texto={t('vazio.meuProgresso')} />
            ) : (
              <ul className="divide-y divide-borda">
                {d.itens.map((m) => (
                  <li key={m.id} className="flex min-w-0 flex-wrap items-center justify-between gap-3 px-4 py-3">
                    <div className="min-w-0 flex-1 basis-60">
                      <Link to={`${BASE}/curso/${m.curso_id}`} className="block text-sm font-medium hover:text-acento-forte">
                        <TextoCortado className="block">
                          {m.curso_titulo}
                        </TextoCortado>
                      </Link>
                      <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-tinta-3">
                        <SeloOrigem m={m} />
                        <span className="numero">{t('progresso.aulasDe', { feitas: m.aulas_concluidas, count: m.aulas_total })}</span>
                        {m.concluida_em && <span className="numero">{t('area.concluidoEm', { data: fmtData(m.concluida_em) })}</span>}
                      </p>
                    </div>
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="w-32 sm:w-40">
                        <BarraProgresso valor={m.percentual} />
                      </div>
                      {m.concluida_em && m.codigo_certificado && (
                        <Botao tamanho="sm" icone={<Award className="size-4" />} onClick={() => navegar(`${BASE}/certificado/${m.codigo_certificado}`)}>
                          {t('acoes.verCertificado')}
                        </Botao>
                      )}
                      {m.concluida_em && !m.codigo_certificado && (
                        <Botao tamanho="sm" variante="primario" icone={<Award className="size-4" />} carregando={emitir.isPending} onClick={() => emitir.mutate(m.curso_id)}>
                          {t('acoes.emitirCertificado')}
                        </Botao>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )
          }
        </Estado>
      )}
    </Cartao>
  );
}

function VerificarCodigo() {
  const { t } = useTranslation('treinamento');
  const navegar = useNavigate();
  const [codigo, setCodigo] = useState('');
  return (
    <Cartao titulo={t('certificado.verificarTitulo')}>
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (codigo.trim()) navegar(`${BASE}/certificado/${encodeURIComponent(codigo.trim().toUpperCase())}`);
        }}
      >
        <p className="w-full text-xs text-tinta-3">{t('certificado.verificarAjuda')}</p>
        <div className="relative min-w-0 flex-1 basis-56">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-tinta-3" aria-hidden />
          <Entrada aria-label={t('certificado.codigo')} placeholder="CERT-XXXX-XXXX-XXXX" className="numero pl-9" value={codigo} onChange={(e) => setCodigo(e.target.value)} maxLength={40} />
        </div>
        <Botao type="submit" disabled={!codigo.trim()}>
          {t('acoes.verificar')}
        </Botao>
      </form>
    </Cartao>
  );
}

function PainelPessoa({ pessoa, aoFechar }: { pessoa: ProgressoPessoa; aoFechar: () => void }) {
  const { t } = useTranslation('treinamento');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [escolhido, setEscolhido] = useState('');
  const [cancelar, setCancelar] = useState<Matricula | null>(null);
  // Val5 (29/10): cancelar a matrícula de outra pessoa pede `treinamento.atribuir`.
  const podeCancelar = useSessao().pode('treinamento.atribuir');
  const invalidar = () => qc.invalidateQueries({ queryKey: ['treinamento'] });
  const q = useQuery({ queryKey: ['treinamento', 'matriculas', 'pessoa', pessoa.id], queryFn: () => getTodos<Matricula>('/treinamento/matriculas', { funcionario_id: pessoa.id }) });
  const cursos = useQuery({ queryKey: ['treinamento', 'cursos', 'publicados'], queryFn: () => getTodos<Curso>('/treinamento/cursos', { publicado: 'true' }) });
  const disponiveis = (cursos.data?.itens ?? []).filter((c) => !q.data?.itens.some((m) => m.curso_id === c.id));

  const matricular = useMutation({
    mutationFn: () => post('/treinamento/matriculas', { curso_id: escolhido, funcionario_id: pessoa.id }),
    onSuccess: async () => {
      setEscolhido('');
      await invalidar();
      toast.sucesso(t('mensagens.matriculado'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const cancelarMatricula = useMutation({
    mutationFn: (id: string) => del(`/treinamento/matriculas/${id}`),
    onSuccess: async () => {
      setCancelar(null);
      await invalidar();
    },
    onError: (e) => {
      setCancelar(null);
      toast.erro(mensagemDeErro(e));
    },
  });

  return (
    <Drawer aberto aoFechar={aoFechar} largura="lg" titulo={nomePessoa(pessoa.nome)}
      rodape={
        <Botao variante="fantasma" onClick={aoFechar}>
          {tn('comum.fechar')}
        </Botao>
      }
    >
      <p className="mb-4 text-sm text-tinta-3">{pessoa.funcao_nome ?? ''}</p>
      <Estado consulta={q}>
        {(d) =>
          d.itens.length === 0 ? (
            <Vazio texto={t('vazio.pessoaSemMatricula')} />
          ) : (
            <ul className="divide-y divide-borda rounded-lg border border-borda">
              {d.itens.map((m) => (
                <li key={m.id} className="flex min-w-0 flex-wrap items-center justify-between gap-3 px-3 py-3">
                  <div className="min-w-0 flex-1 basis-52">
                    <p className="truncate text-sm font-medium" title={m.curso_titulo}>{m.curso_titulo}</p>
                    <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-tinta-3">
                      <SeloOrigem m={m} />
                      <span className="numero">{t('progresso.aulasDe', { feitas: m.aulas_concluidas, count: m.aulas_total })}</span>
                      {m.concluida_em ? <Selo cor="verde">{t('selos.concluido')}</Selo> : <Selo cor="azul">{t('selos.emAndamento')}</Selo>}
                      {m.codigo_certificado && <span className="numero">{m.codigo_certificado}</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-28">
                      <BarraProgresso valor={m.percentual} />
                    </div>
                    {!m.concluida_em && podeCancelar && <BotaoIcone rotulo={t('acoes.cancelarMatricula')} onClick={() => setCancelar(m)} icone={<Trash2 className="size-4" />} perigo />}
                  </div>
                </li>
              ))}
            </ul>
          )
        }
      </Estado>
      <section aria-label={t('progresso.matricularEm')} className="mt-6 border-t border-borda pt-4">
        <h3 className="kicker mb-3">{t('progresso.matricularEm')}</h3>
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-0 flex-1 basis-56">
            <Selecao aria-label={t('progresso.matricularEm')} value={escolhido} vazio={tn('comum.selecione')} onChange={(e) => setEscolhido(e.target.value)} opcoes={disponiveis.map((c) => ({ valor: c.id, rotulo: c.titulo }))} />
          </div>
          <Botao icone={<Plus className="size-4" />} disabled={!escolhido} carregando={matricular.isPending} onClick={() => matricular.mutate()}>
            {t('acoes.matricular')}
          </Botao>
        </div>
      </section>
      <Confirmar aberto={!!cancelar} aoFechar={() => setCancelar(null)} aoConfirmar={() => cancelar && cancelarMatricula.mutate(cancelar.id)} titulo={t('confirmar.cancelarMatricula')} texto={t('confirmar.cancelarMatriculaTexto')} perigoso carregando={cancelarMatricula.isPending} />
    </Drawer>
  );
}

function PainelCurso({ curso, aoFechar }: { curso: ProgressoPorCurso; aoFechar: () => void }) {
  const { t } = useTranslation('treinamento');
  const { t: tn } = useTranslation();
  const navegar = useNavigate();
  const q = useQuery({ queryKey: ['treinamento', 'matriculas', 'curso', curso.id], queryFn: () => getTodos<Matricula>('/treinamento/matriculas', { curso_id: curso.id }) });
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={curso.titulo}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.fechar')}
          </Botao>
          <Botao onClick={() => navegar(`${BASE}/curso/${curso.id}`)}>{t('acoes.abrirCurso')}</Botao>
        </>
      }
    >
      <Estado consulta={q}>
        {(d) =>
          d.itens.length === 0 ? (
            <Vazio texto={t('vazio.cursoSemMatricula')} />
          ) : (
            <ul className="divide-y divide-borda rounded-lg border border-borda">
              {d.itens.map((m) => (
                <li key={m.id} className="flex min-w-0 flex-wrap items-center justify-between gap-3 px-3 py-3">
                  <div className="min-w-0 flex-1 basis-52">
                    <p className="truncate text-sm font-medium" title={nomePessoa(m.funcionario_nome)}>{nomePessoa(m.funcionario_nome)}</p>
                    <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-tinta-3">
                      <SeloOrigem m={m} />
                      <span className="numero">{t('progresso.aulasDe', { feitas: m.aulas_concluidas, count: m.aulas_total })}</span>
                      {m.concluida_em ? <Selo cor="verde">{t('selos.concluido')}</Selo> : <Selo cor="azul">{t('selos.emAndamento')}</Selo>}
                    </p>
                  </div>
                  <div className="w-28">
                    <BarraProgresso valor={m.percentual} />
                  </div>
                </li>
              ))}
            </ul>
          )
        }
      </Estado>
    </Drawer>
  );
}
